import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { usePageEntityContext } from '@/lib/pageEntityContext';
import { recentRecordsStore } from '@/components/GlobalSearch/commands/recentRecordsStore';

/**
 * Records which entity records the user opens, for the command palette's history.
 *
 * Mounted once in the app shell rather than in the list view, because a list is only one
 * of the ways into a record: global search, a deep link, a related-view panel and the
 * palette itself all arrive without passing through a grid. Watching the route catches
 * every one of them.
 */
export function useRecordVisitTracker(): void {
  const { module, entityType, entityId, displayField } = usePageEntityContext();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!module || !entityType || !entityId) return undefined;

    const base = { module, entityType, entityId, path: pathname };
    const read = () => readRecordLabel(queryClient, entityId, displayField);

    // Record immediately so a visit counts even if its data never loads; the label is
    // whatever is known now, which on a fresh navigation is usually nothing.
    recentRecordsStore.record({ ...base, label: read() });
    if (read()) return undefined;

    // The record almost always lands a moment after navigation. Watch for it once, then
    // stop — this is a label upgrade, not an ongoing subscription.
    let done = false;
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      if (done) return;
      const label = read();
      if (!label) return;
      done = true;
      recentRecordsStore.record({ ...base, label });
      unsubscribe();
    });
    return unsubscribe;
  }, [module, entityType, entityId, displayField, pathname, queryClient]);
}

/**
 * Pull the record's business identity out of the query cache.
 *
 * Matched by key shape rather than an exact key: the generated factories produce
 * `[plural, tenantId, 'detail', id]` and the plural is not derivable (`NumberSeries` does
 * not pluralise to `numberseriess`). The id being last is guaranteed by the factory.
 */
function readRecordLabel(
  queryClient: QueryClient,
  entityId: string,
  displayField: string | undefined,
): string | undefined {
  if (!displayField) return undefined;
  const entries = queryClient.getQueriesData<Record<string, unknown>>({
    predicate: (q) => q.queryKey.includes('detail') && q.queryKey[q.queryKey.length - 1] === entityId,
  });
  for (const [, data] of entries) {
    const value = data?.[displayField];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}
